/**
 * Strip characters that are not allowed in the estimate field (digits, spaces, w/d/h/m only).
 * Use on **change** / **paste** so users cannot type or keep letters like f, r, x, punctuation, etc.
 *
 * @param {unknown} value
 * @returns {string}
 */
export function sanitizeWorkItemEstimateInput(value) {
  if (value == null) return "";
  return String(value).replace(/[^\d\swdhm]/gi, "");
}

/**
 * Parse "Original estimate" text for work items into API minutes.
 *
 * Units (common software planning): **w** = 5×8h work week, **d** = 8h work day,
 * **h** = hour, **m** = minute. Examples: `2w`, `5h`, `1d 2h`, `2w 1d 5h 4m`.
 *
 * **Plain digits** (e.g. `120`) are treated as **minutes** for backward compatibility
 * with older numeric-only entry.
 *
 * With units, the string may contain **only** digits, spaces, and the letters **w, d, h, m**
 * (any case). Any other character (e.g. `x`, commas, decimals) is rejected.
 *
 * @param {unknown} input
 * @returns {{ ok: true, minutes: number } | { ok: false, minutes: 0, error: string }}
 */
export function parseWorkItemEstimateInputToMinutes(input) {
  const fail = (error) => ({ ok: false, minutes: 0, error });

  if (input == null) return { ok: true, minutes: 0 };
  const str = String(input).trim();
  if (!str) return { ok: true, minutes: 0 };

  if (/^\d+$/.test(str)) {
    const n = parseInt(str, 10);
    if (!Number.isFinite(n) || n < 0) {
      return fail("Enter a non-negative number of minutes, or use units (e.g. 8h, 2d).");
    }
    return { ok: true, minutes: n };
  }

  // Duration mode: only digits, spaces, and unit letters w / d / h / m (case-insensitive).
  const onlyAllowedChars = /^[\d\swdhm]+$/i.test(str);
  if (!onlyAllowedChars) {
    return fail("Only w, d, h, m are allowed as units, with numbers and spaces (e.g. 2w 1d 5h).");
  }

  const MINUTES_PER_HOUR = 60;
  const MINUTES_PER_WORK_DAY = 8 * MINUTES_PER_HOUR;
  const MINUTES_PER_WORK_WEEK = 5 * MINUTES_PER_WORK_DAY;

  const mult = {
    w: MINUTES_PER_WORK_WEEK,
    d: MINUTES_PER_WORK_DAY,
    h: MINUTES_PER_HOUR,
    m: 1,
  };

  const tokenRe = /(\d+)\s*([wdhm])\b/gi;
  let total = 0;
  let matched = false;
  for (const m of str.matchAll(tokenRe)) {
    matched = true;
    const num = parseInt(m[1], 10);
    if (!Number.isFinite(num) || num < 0) {
      return fail("Invalid number in estimate.");
    }
    const u = m[2].toLowerCase();
    if (mult[u] == null) return fail("Invalid unit in estimate.");
    total += num * mult[u];
  }

  if (!matched) {
    return fail("Use units like 2w, 1d, 8h, 45m, or minutes only as digits (e.g. 120).");
  }

  const remainder = str.replace(tokenRe, "").replace(/\s+/g, "");
  if (remainder.length > 0) {
    return fail("Invalid estimate. Try e.g. 2w 1d 5h 30m.");
  }

  if (!Number.isFinite(total) || total < 0) {
    return fail("Invalid estimate.");
  }

  return { ok: true, minutes: Math.round(total) };
}
